// src/pages/VerifyEmailPage.tsx
// Public route — reached from the link in the verification email (either a
// new-account verification or a pending email-change confirmation, chosen by
// the ?change=1 flag authService.authController appends for the latter).
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { authService } from 'services/authService';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const isChange = searchParams.get('change') === '1';

  const [status, setStatus]   = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Missing verification token.');
      return;
    }
    const call = isChange ? authService.verifyEmailChange(token) : authService.verifyEmail(token);
    call
      .then((res) => { setStatus('success'); setMessage(res.message); })
      .catch((err: any) => {
        setStatus('error');
        setMessage(err?.response?.data?.message || 'Verification failed — the link may have expired.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isChange]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[var(--om-app)] p-6">
      <div className="w-full max-w-md bg-[var(--om-panel)] border border-[var(--om-border)] rounded-3xl p-10 shadow-2xl text-center">
        <div className="flex justify-center mb-4">
          {status === 'loading' ? <Loader2 className="w-10 h-10 text-blue-400 animate-spin" strokeWidth={2} />
            : status === 'success' ? <CheckCircle2 className="w-10 h-10 text-green-400" strokeWidth={2} />
            : <XCircle className="w-10 h-10 text-red-400" strokeWidth={2} />}
        </div>
        <h1 className="text-xl font-black text-[var(--om-text)] mb-2">
          {status === 'loading' ? 'Verifying...' : status === 'success' ? 'Verified!' : 'Verification Failed'}
        </h1>
        <p className="text-sm text-[var(--om-text-dim)] mb-6">{message}</p>
        <Link
          to="/login"
          className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm rounded-xl transition-all"
        >
          Go to Login
        </Link>
      </div>
    </div>
  );
}
